import { Container, Graphics } from "pixi.js"
import { Entity } from "game-common/models"
import { BODY_GROUND_OFFSET } from "game-common/lpc/body_placement"

// The body finishes on the authoritative deadline; disturbed soil gets time to settle afterward.
const SOIL_FADE_TAIL_MS = 350

/** Shared lifecycle for arrivals on the current floor, other floors and observation views. */
export function updatePreSpawnEffect(entity: Entity, body: Container, surface: Container,
    effects: Map<string, GroundRupture>, completed: Set<string>, onStart?: () => void): void {
    let effect = effects.get(entity.entityId)
    if (!effect && !completed.has(entity.entityId) && entity.preSpawnEffectView?.type === "ground-rupture") {
        effect = new GroundRupture(surface, body, entity)
        effects.set(entity.entityId, effect)
        if (entity.hp > 0 && !entity.isDead() && entity.preSpawnEffectView.remainingMs > 0) onStart?.()
    }
    if (effect && !effect.update(entity)) {
        effect.destroy()
        effects.delete(entity.entityId)
        completed.add(entity.entityId)
    }
}

export function sweepPreSpawnEffects(effects: Map<string, GroundRupture>, completed: Set<string>,
    visible: (id: string) => boolean): void {
    effects.forEach((effect, id) => {
        effect.art.visible = effect.debris.visible = !!visible(id)
        if (effect.isExpired()) {
            effect.destroy()
            effects.delete(id)
            completed.add(id)
        }
    })
}

/** World-space art and clipping: the body rises through a fixed ground line. No timers. */
export class GroundRupture {
    readonly art = new Graphics()
    readonly debris = new Graphics()
    readonly clip = new Graphics()
    private endsAt: number
    private durationMs: number
    private previousMask: Container["mask"]
    private anchor: { x: number; y: number; groundY: number; size: number; zIndex: number }
    private bodyInHole = true
    private rim: number[] = []
    private clods: { angle: number; delay: number; life: number; kick: number; lift: number; size: number; spin: number; color: number }[] = []

    constructor(surface: Container, private body: Container, entity: Entity, now = Date.now()) {
        this.durationMs = entity.preSpawnEffectView.durationMs
        this.endsAt = now + entity.preSpawnEffectView.remainingMs
        this.previousMask = body.mask
        this.anchor = {
            x: entity.location.x, y: entity.location.y,
            groundY: entity.location.y + BODY_GROUND_OFFSET,
            size: Math.max(0.5, entity.spriteScale || 1), zIndex: body.zIndex,
        }
        // Stable variation per spawn, so shapes neither flicker per frame nor repeat across a wave.
        let seed = 2166136261
        for (const ch of entity.entityId) seed = Math.imul(seed ^ ch.charCodeAt(0), 16777619)
        const random = () => {
            seed = Math.imul(seed, 1664525) + 1013904223 | 0
            return (seed >>> 0) / 4294967296
        }
        this.rim = Array.from({ length: 24 }, () => 0.82 + random() * 0.3)
        const earth = [0x423022, 0x60442d, 0x79573a, 0x97714b]
        this.clods = Array.from({ length: 18 }, (_, i) => ({
            angle: random() * Math.PI * 2,
            delay: 0.08 + random() * 0.3,
            life: 0.35 + random() * 0.35,
            kick: 10 + random() * 24,
            lift: 8 + random() * 22,
            size: 1 + random() * 2.5,
            spin: (random() - 0.5) * 6,
            color: earth[i % earth.length],
        }))
        // This is mask geometry only; it must never become ordinary world art.
        const { x, groundY, size } = this.anchor
        this.clip.beginFill(0xffffff).drawRect(-256 * size, -512 * size, 512 * size, 512 * size).endFill()
        this.clip.position.set(x, groundY)
        surface.addChild(this.art, this.debris, this.clip)
        body.mask = this.clip
    }

    // Called after the renderer sets the body's ordinary location each frame.
    update(entity: Entity, now = Date.now()): boolean {
        const view = entity.preSpawnEffectView
        if (entity.isDead() || entity.hp <= 0) return false
        // A repeated/stale snapshot must never restart or extend the effect.
        if (view) this.endsAt = Math.min(this.endsAt, now + view.remainingMs)
        // An expired wire view ends emergence, not the local soil's fade-out.
        if (this.isExpired(now)) return false
        const remaining = this.endsAt - now
        const age = Math.max(0, 1 - remaining / this.durationMs)
        const t = Math.min(1, age)
        const { x, y, groundY, size, zIndex } = this.anchor
        // Once displaced beyond the opening, release the body rather than dragging the hole
        // or cutting a moved zombie in half at the original ground line.
        const dx = (entity.location.x - x) / (22 * size)
        const dy = (entity.location.y - y) / (8 * size)
        if (this.bodyInHole && (remaining <= 0 || dx * dx + dy * dy > 1)) {
            this.releaseBody()
        }
        const rise = Math.max(0, Math.min(1, (t - 0.16) / 0.74))
        // Hesitate under the crust, push upward, then settle into the standing position.
        const push = rise * rise * (3 - 2 * rise)
        if (this.bodyInHole) this.body.y += (1 - push) * 64 * size
        this.art.position.set(x, groundY)
        this.art.zIndex = zIndex - 1
        this.debris.zIndex = zIndex + 1
        this.debris.position.copyFrom(this.art.position)
        this.debris.scale.set(size)
        this.debris.visible = this.body.visible
        this.debris.clear()
        this.art.scale.set(size)
        this.art.visible = this.body.visible
        this.art.clear()
        const fadeLeadMs = this.durationMs * 0.2
        const fadeProgress = Math.max(0, Math.min(1,
            (now - (this.endsAt - fadeLeadMs)) / (fadeLeadMs + SOIL_FADE_TAIL_MS)))
        const fade = 1 - fadeProgress * fadeProgress * (3 - 2 * fadeProgress)
        const open = Math.min(1, t / 0.26)

        // Broken soil around a recessed, irregular opening. The inner dark edge is below the body.
        const outline: number[] = [], inner: number[] = []
        this.rim.forEach((r, i) => {
            const a = i * Math.PI * 2 / this.rim.length
            outline.push(Math.cos(a) * 22 * r * open, Math.sin(a) * 8 * r * open)
            inner.push(Math.cos(a) * 17 * r * open, Math.sin(a) * 5.5 * r * open - 1)
        })
        this.art.beginFill(0x60452f, fade * 0.75).drawPolygon(outline).endFill()
        this.art.beginFill(0x211b16, fade * 0.92).drawPolygon(inner).endFill()
        this.art.beginFill(0x100e0b, fade * 0.45).drawEllipse(0, -1, 12 * open, 3.5 * open).endFill()

        // Sparse branching cracks, rather than a symmetrical starburst.
        for (let i = 0; i < 6; i++) {
            const clod = this.clods[i], a = clod.angle, c = Math.cos(a), s = Math.sin(a)
            const reach = (23 + clod.kick * 0.35) * open
            this.art.lineStyle(1, 0x352a20, fade * 0.7)
            this.art.moveTo(c * 17 * open, s * 6 * open)
            this.art.lineTo(c * reach * 0.8 + s * 2, s * reach * 0.3)
            this.art.lineTo(c * reach, s * reach * 0.4)
        }
        this.art.lineStyle(0)

        // Only the near lip overlaps the feet. Angular soil plates make the hole read as depth.
        for (let i = 1; i < 12; i++) {
            const a = i * Math.PI / 12, r = this.rim[i]
            const x = Math.cos(a) * 19 * r * open, y = Math.sin(a) * 6.5 * r * open
            this.debris.beginFill(i % 2 ? 0x755338 : 0x523c29, fade)
            this.debris.drawPolygon([x - 3, y - 1, x - 1, y - 3, x + 3, y - 1, x + 2, y + 2, x - 2, y + 2]).endFill()
        }

        for (const clod of this.clods) {
            const u = (age - clod.delay) / clod.life
            if (u < 0 || u >= 1) continue
            const c = Math.cos(clod.angle), s = Math.sin(clod.angle)
            const travel = clod.kick * (1 - Math.exp(-3 * u))
            const x = c * (12 * open + travel)
            const floorY = s * (4 * open + travel * 0.35)
            // Parabolic height is separate from the ground footprint: clods fall back to earth.
            const y = floorY - 4 * clod.lift * u * (1 - u)
            const alpha = fade * Math.min(1, (1 - u) / 0.2)
            const layer = s < 0 ? this.art : this.debris
            const points: number[] = []
            for (let j = 0; j < 5; j++) {
                const a = j * Math.PI * 2 / 5 + u * clod.spin
                const r = clod.size * (j % 2 ? 0.65 : 1)
                points.push(x + Math.cos(a) * r, y + Math.sin(a) * r)
            }
            layer.beginFill(clod.color, alpha).drawPolygon(points).endFill()
            // A lit upper facet and grounded shadow distinguish solid dirt from flat confetti.
            layer.beginFill(0xb18a5c, alpha * 0.5).drawRect(x - clod.size * 0.4, y - clod.size * 0.6, clod.size, 0.8).endFill()
            this.art.beginFill(0x211b16, alpha * 0.12 * (1 - u)).drawEllipse(x, floorY, clod.size * 1.2, 0.7).endFill()

            // Low, diffuse dust spreads sideways after each break through the crust.
            const dustAlpha = Math.sin(u * Math.PI) * fade * 0.045
            const dustY = floorY - Math.sin(u * Math.PI) * 4
            const radius = 2 + u * 7
            for (let j = 0; j < 3; j++) {
                layer.beginFill(0x9c805c, dustAlpha * (1 - j * 0.22))
                layer.drawEllipse(x + c * u * 5, dustY, radius + j * 2, (radius + j * 2) * 0.55).endFill()
            }
        }
        return true
    }

    isExpired(now = Date.now()) {
        return now >= this.endsAt + SOIL_FADE_TAIL_MS
    }

    private releaseBody() {
        this.bodyInHole = false
        if (this.body.mask === this.clip) this.body.mask = this.previousMask
        // Pixi's mask setter restores renderable=true when the mask loses its last owner.
        // Remove and clear it immediately, including during the soil's fade tail.
        this.clip.parent?.removeChild(this.clip)
        this.clip.clear()
        this.clip.renderable = false
        this.clip.visible = false
    }

    destroy() {
        this.releaseBody()
        this.art.parent?.removeChild(this.art)
        this.debris.parent?.removeChild(this.debris)
        this.clip.parent?.removeChild(this.clip)
        this.art.destroy()
        this.debris.destroy()
        this.clip.destroy()
    }
}
